import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Building2, ArrowRight } from 'lucide-react';
import { TENANT_CONFIG, TenantId } from '../config/tenant';
import { useI18n } from '../i18n';
import { supabase } from '../config/supabase';
import { ipDetectionService } from '../services/ip-detection.service';
import Loader from '../components/shared/Progressbar';
import { withTimeout } from '../utils/with-timeout';
import '../styles/auth.css';

const TENANT_AUTH_TIMEOUT_MS = 4000;
const TENANT_IP_TIMEOUT_MS = 2500;

/**
 * 租户选择页面
 * - 已登录用户：自动重定向到其租户门户
 * - 有偏好租户的访客：自动重定向到偏好租户门户
 * - 新访客：根据 IP 检测语言，显示选择页面
 */
const TenantSelectPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, lang, switchTo } = useI18n();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    checkUserAndRedirect();
  }, []);

  const checkUserAndRedirect = async () => {
    try {
      // 1. 检查用户是否已登录
      const { data: { session } } = await withTimeout(
        supabase.auth.getSession(),
        TENANT_AUTH_TIMEOUT_MS,
        'Tenant session check',
      );

      if (session) {
        // 已登录用户：获取用户的租户信息
        const { data: profile } = await withTimeout(
          supabase
            .from('v_fs_users')
            .select('tenant')
            .eq('id', session.user.id)
            .single(),
          TENANT_AUTH_TIMEOUT_MS,
          'Tenant profile lookup',
        );

        if (profile?.tenant) {
          // 用户有租户信息，直接跳转
          setIsChecking(false);
          navigate(`/${profile.tenant}/portal`, { replace: true });
          return;
        }
      }

      // 2. 检查是否有偏好租户（localStorage）
      const preferredTenant = localStorage.getItem('preferred_tenant') as TenantId | null;
      if (preferredTenant && (preferredTenant === 'cn' || preferredTenant === 'intl')) {
        // 有偏好租户，自动跳转
        setIsChecking(false);
        navigate(`/${preferredTenant}/portal`, { replace: true });
        return;
      }

      // 3. 没有登录也没有偏好，根据 IP 检测语言
      try {
        const recommendedLang = await withTimeout(
          ipDetectionService.getRecommendedLanguage(),
          TENANT_IP_TIMEOUT_MS,
          'Tenant IP language detection',
        );
        console.log('🌐 根据 IP 检测推荐语言:', recommendedLang);

        // 如果检测到的语言与当前语言不同，切换语言
        if (recommendedLang !== lang) {
          switchTo(recommendedLang);
        }
      } catch (ipError) {
        console.error('IP 检测失败，使用默认语言:', ipError);
      }

      // 4. 显示选择页面
      setIsChecking(false);
    } catch (error) {
      console.error('检查用户状态失败:', error);
      setIsChecking(false);
    }
  };

  const handleSelectTenant = (tenantId: TenantId) => {
    // 记住用户选择
    localStorage.setItem('preferred_tenant', tenantId);
    // 跳转到对应租户的门户
    navigate(`/${tenantId}/portal`);
  };

  const tenantCards: { id: TenantId; icon: React.ReactNode; gradient: string; hoverGradient: string }[] = [
    {
      id: 'cn',
      icon: <Building2 className="w-12 h-12" />,
      gradient: 'from-red-500 via-orange-500 to-yellow-500',
      hoverGradient: 'group-hover:from-red-600 group-hover:via-orange-600 group-hover:to-yellow-600',
    },
    {
      id: 'intl',
      icon: <Globe className="w-12 h-12" />,
      gradient: 'from-blue-500 via-purple-500 to-pink-500',
      hoverGradient: 'group-hover:from-blue-600 group-hover:via-purple-600 group-hover:to-pink-600',
    },
  ];

  // 检查中显示加载状态
  if (isChecking) {
    return (
      <div className="auth-background light-mode">
        <div className="auth-orb auth-orb-1" />
        <div className="auth-orb auth-orb-2" />
        <div className="auth-orb auth-orb-3" />
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex flex-col items-center gap-6">
            <Loader />
            <p className="text-gray-500 text-sm">
              {lang === 'zh' ? '正在检查登录状态...' : 'Checking login status...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-background light-mode">
      {/* Animated gradient orbs */}
      <div className="auth-orb auth-orb-1" />
      <div className="auth-orb auth-orb-2" />
      <div className="auth-orb auth-orb-3" />
      
      {/* Main content */}
      <div className="min-h-screen flex flex-col items-center justify-center p-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <img 
              src="/logo_24.svg" 
              alt="Unilumin" 
              className="h-12 object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">
            {lang === 'zh' ? '欢迎使用洲明文件系统' : 'Welcome to Unilumin File System'}
          </h1>
          <p className="text-gray-600 text-lg">
            {lang === 'zh' ? '请选择您要访问的区域' : 'Please select your region'}
          </p>
        </div>

        {/* Tenant Cards */}
        <div className="flex flex-col md:flex-row gap-6 max-w-4xl w-full">
          {tenantCards.map(({ id, icon, gradient, hoverGradient }) => (
            <button
              key={id}
              onClick={() => handleSelectTenant(id)}
              className="group flex-1 relative overflow-hidden rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-blue-500"
            >
              {/* Background gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${gradient} ${hoverGradient} transition-all duration-300`} />
              
              {/* Content */}
              <div className="relative p-8 md:p-10 text-white">
                {/* Icon */}
                <div className="flex justify-center mb-6">
                  <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm group-hover:scale-110 transition-transform duration-300">
                    {icon}
                  </div>
                </div>

                {/* Flag & Title */}
                <div className="text-center mb-4">
                  <span className="text-5xl mb-4 block">{TENANT_CONFIG[id].flag}</span>
                  <h2 className="text-2xl font-bold mb-2">
                    {t(`tenant.${id}`)}
                  </h2>
                  <p className="text-white/80 text-sm">
                    {TENANT_CONFIG[id].displayName}
                  </p>
                </div>

                {/* Arrow indicator */}
                <div className="flex justify-center mt-6">
                  <div className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full group-hover:bg-white/30 transition-colors">
                    <span className="text-sm font-medium">
                      {lang === 'zh' ? '进入' : 'Enter'}
                    </span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
              <div className="absolute -top-8 -left-8 w-24 h-24 bg-white/10 rounded-full blur-xl" />
            </button>
          ))}
        </div>

        {/* Language Switcher */}
        <div className="mt-12">
          <button
            onClick={() => switchTo(lang === 'zh' ? 'en' : 'zh')}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-white/50 rounded-lg transition-colors"
          >
            <Globe className="w-4 h-4" />
            <span>{lang === 'zh' ? 'English' : '中文'}</span>
          </button>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>© 2025 Unilumin Group. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default TenantSelectPage;
